// src/services/environmentDatabase.js
import Dexie from 'dexie';

class EnvironmentDatabaseService extends Dexie {
  constructor() {
    super('TegimEnvironmentsDB');
    
    // Database schema
    this.version(1).stores({
      environments: 'id, name, description, color, base_url, environment_type, hidden_fields, is_default, is_active, created_at, updated_at, created_by',
      environment_variables: '++id, environment_id, key_name, key_value, variable_type, is_secret, description, created_at, updated_at',
      environment_templates: '++id, name, template_type, default_variables, hidden_fields, color, is_system, created_at',
      environment_usage_logs: '++id, environment_id, action_type, details, created_at'
    });

    this.isInitialized = false;
  }

  // Database'i başlat
  async initialize() {
    try {
      await this.open();
      await this.seedDefaultData();
      this.isInitialized = true;
      console.log('Environment Database initialized successfully');
    } catch (error) {
      console.error('Environment Database initialization failed:', error);
      throw error;
    }
  }

  // Varsayılan verileri yükle
  async seedDefaultData() {
    try {
      const templateCount = await this.environment_templates.count();
      if (templateCount === 0) {
        await this.environment_templates.bulkAdd([
          {
            name: 'Frontend Development',
            description: 'Frontend geliştirme için optimize edilmiş environment',
            template_type: 'frontend',
            default_variables: {
              "API_URL": "http://localhost:9071",
              "APP_ENV": "development",
              "DEBUG": "true"
            },
            hidden_fields: ["id", "createdAt", "updatedAt", "userId"],
            color: 'bg-blue-500',
            is_system: true,
            created_at: new Date().toISOString()
          },
          {
            name: 'Backend Development',
            description: 'Backend geliştirme için tam özellikli environment',
            template_type: 'backend',
            default_variables: {
              "API_URL": "http://localhost:9072",
              "DB_HOST": "localhost",
              "DB_PORT": "5432",
              "LOG_LEVEL": "debug"
            },
            hidden_fields: [],
            color: 'bg-green-500',
            is_system: true,
            created_at: new Date().toISOString()
          },
          {
            name: 'Production Frontend',
            description: 'Production frontend environment',
            template_type: 'frontend',
            default_variables: {
              "API_URL": "https://api.production.com",
              "APP_ENV": "production",
              "DEBUG": "false"
            },
            hidden_fields: ["id", "createdAt", "updatedAt", "internalId", "version"],
            color: 'bg-red-500',
            is_system: true,
            created_at: new Date().toISOString()
          }
        ]);
      }

      const envCount = await this.environments.count();
      if (envCount === 0) {
        await this.createDefaultEnvironments();
      }
    } catch (error) {
      console.error('Failed to seed default data:', error);
    }
  }

  // Varsayılan environment'ları oluştur
  async createDefaultEnvironments() {
    const defaultEnvs = [
      {
        id: 'dev-frontend',
        name: 'Development Frontend',
        description: 'Frontend geliştirme ortamı',
        color: 'bg-blue-500',
        base_url: 'http://localhost:9071',
        environment_type: 'frontend',
        hidden_fields: ["id", "createdAt", "updatedAt"],
        is_default: true,
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 'dev-backend',
        name: 'Development Backend',
        description: 'Backend geliştirme ortamı',
        color: 'bg-green-500',
        base_url: 'http://localhost:9072',
        environment_type: 'backend',
        hidden_fields: [],
        is_default: false,
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    ];

    await this.environments.bulkAdd(defaultEnvs);

    // Varsayılan değişkenleri ekle
    await this.environment_variables.bulkAdd([
      {
        environment_id: 'dev-frontend',
        key_name: 'API_URL',
        key_value: 'http://localhost:9071/api',
        variable_type: 'string',
        is_secret: false,
        description: 'Frontend API base URL',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        environment_id: 'dev-frontend',
        key_name: 'DEBUG',
        key_value: 'true',
        variable_type: 'boolean',
        is_secret: false,
        description: 'Debug modu',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        environment_id: 'dev-backend',
        key_name: 'API_URL',
        key_value: 'http://localhost:9072/api/v1',
        variable_type: 'string',
        is_secret: false,
        description: 'Backend API base URL',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    ]);
  }

  // Environment CRUD işlemleri
  async createEnvironment(envData) {
    if (!this.isInitialized) await this.initialize();
    
    const environment = {
      ...envData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      is_active: true
    };

    await this.environments.add(environment);
    
    // Log the action
    await this.logEnvironmentAction(envData.id, 'create', { environment });
    
    return environment;
  }

  async getEnvironment(id) {
    if (!this.isInitialized) await this.initialize();
    return await this.environments.get(id);
  }

  async getAllEnvironments() {
    if (!this.isInitialized) await this.initialize();
    // Dexie/IndexedDB 'boolean' indeks anahtarı geçersiz olabilir; güvenli yol: tümünü alıp filtrele
    const all = await this.environments.toArray();
    return all.filter((e) => e && e.is_active === true);
  }

  async updateEnvironment(id, updates) {
    if (!this.isInitialized) await this.initialize();
    
    const updatedData = {
      ...updates,
      updated_at: new Date().toISOString()
    };

    await this.environments.update(id, updatedData);
    
    // Log the action
    await this.logEnvironmentAction(id, 'update', { updates: updatedData });
    
    return updatedData;
  }

  async deleteEnvironment(id) {
    if (!this.isInitialized) await this.initialize();
    
    // Soft delete
    await this.environments.update(id, { 
      is_active: false, 
      updated_at: new Date().toISOString() 
    });
    
    // Also deactivate variables
    await this.environment_variables.where('environment_id').equals(id).delete();
    
    // Log the action
    await this.logEnvironmentAction(id, 'delete', {});
  }

  // Variable CRUD işlemleri
  async setVariable(environmentId, key, value, options = {}) {
    if (!this.isInitialized) await this.initialize();
    
    const variable = {
      environment_id: environmentId,
      key_name: key,
      key_value: value,
      variable_type: options.type || 'string',
      is_secret: options.isSecret || false,
      description: options.description || '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    // Check if variable exists
    const existing = await this.environment_variables
      .where({ environment_id: environmentId, key_name: key })
      .first();

    if (existing) {
      await this.environment_variables.update(existing.id, {
        key_value: value,
        variable_type: options.type || existing.variable_type,
        is_secret: options.isSecret !== undefined ? options.isSecret : existing.is_secret,
        description: options.description || existing.description,
        updated_at: new Date().toISOString()
      });
    } else {
      await this.environment_variables.add(variable);
    }

    // Log the action
    await this.logEnvironmentAction(environmentId, 'variable_change', { 
      key, 
      action: existing ? 'update' : 'create' 
    });
  }

  async getVariable(environmentId, key) {
    if (!this.isInitialized) await this.initialize();
    
    const variable = await this.environment_variables
      .where({ environment_id: environmentId, key_name: key })
      .first();
    
    return variable?.key_value;
  }

  async getAllVariables(environmentId) {
    if (!this.isInitialized) await this.initialize();
    
    const variables = await this.environment_variables
      .where('environment_id')
      .equals(environmentId)
      .toArray();
    
    const result = {};
    variables.forEach(variable => {
      result[variable.key_name] = variable.key_value;
    });
    
    return result;
  }

  async deleteVariable(environmentId, key) {
    if (!this.isInitialized) await this.initialize();
    
    await this.environment_variables
      .where({ environment_id: environmentId, key_name: key })
      .delete();
    
    // Log the action
    await this.logEnvironmentAction(environmentId, 'variable_change', { 
      key, 
      action: 'delete' 
    });
  }

  // Template işlemleri
  async getTemplates(type = null) {
    if (!this.isInitialized) await this.initialize();
    
    if (type) {
      return await this.environment_templates
        .where('template_type')
        .equals(type)
        .toArray();
    }
    
    return await this.environment_templates.toArray();
  }

  async createEnvironmentFromTemplate(templateId, environmentData) {
    if (!this.isInitialized) await this.initialize();
    
    const template = await this.environment_templates.get(templateId);
    if (!template) {
      throw new Error('Şablon bulunamadı');
    }

    const environment = {
      ...environmentData,
      environment_type: template.template_type,
      hidden_fields: template.hidden_fields,
      color: template.color
    };

    const createdEnv = await this.createEnvironment(environment);

    // Add default variables from template
    if (template.default_variables) {
      for (const [key, value] of Object.entries(template.default_variables)) {
        await this.setVariable(createdEnv.id, key, value);
      }
    }

    return createdEnv;
  }

  // Field filtering (frontend/backend için gizleme)
  filterRequestBody(environmentId, body) {
    return new Promise(async (resolve) => {
      if (!this.isInitialized) await this.initialize();
      
      const environment = await this.getEnvironment(environmentId);
      if (!environment || !environment.hidden_fields || environment.hidden_fields.length === 0) {
        resolve(body);
        return;
      }

      const filtered = { ...body };
      environment.hidden_fields.forEach(field => {
        delete filtered[field];
      });

      resolve(filtered);
    });
  }

  // Usage logging
  async logEnvironmentAction(environmentId, actionType, details = {}) {
    try {
      await this.environment_usage_logs.add({
        environment_id: environmentId,
        action_type: actionType,
        details: details,
        created_at: new Date().toISOString()
      });
    } catch (error) {
      console.warn('Failed to log environment action:', error);
    }
  }

  // Statistics
  async getEnvironmentStats() {
    if (!this.isInitialized) await this.initialize();
    
    const totalEnvironments = await this.environments.count();
    const activeEnvironments = await this.environments.where('is_active').equals(true).count();
    const totalVariables = await this.environment_variables.count();
    
    return {
      totalEnvironments,
      activeEnvironments,
      totalVariables
    };
  }

  // Export/Import
  async exportEnvironment(environmentId) {
    if (!this.isInitialized) await this.initialize();
    
    const environment = await this.getEnvironment(environmentId);
    const variables = await this.environment_variables
      .where('environment_id')
      .equals(environmentId)
      .toArray();
    
    return {
      environment,
      variables: variables.map(v => ({
        key_name: v.key_name,
        key_value: v.key_value,
        variable_type: v.variable_type,
        is_secret: v.is_secret,
        description: v.description
      }))
    };
  }

  async importEnvironment(data) {
    if (!this.isInitialized) await this.initialize();
    
    // Create environment
    const environment = await this.createEnvironment(data.environment);
    
    // Import variables
    if (data.variables && data.variables.length > 0) {
      for (const variable of data.variables) {
        await this.setVariable(
          environment.id,
          variable.key_name,
          variable.key_value,
          {
            type: variable.variable_type,
            isSecret: variable.is_secret,
            description: variable.description
          }
        );
      }
    }
    
    return environment;
  }
}

// Singleton instance
const environmentDB = new EnvironmentDatabaseService();

export default environmentDB;
